import React, { useState, useEffect } from "react";
import { API_BASE_URL } from "../../config";
import axios from "../../api/axiosInstance";
import {FaCheck} from "react-icons/fa";

const CheckMark = FaCheck as unknown as React.FC;

type CurrentPlanResponse = {
    plan_name: string;
    is_payment_valid: boolean;
};
type CheckoutResponse = {
    url?: string;
    error?: string;
};
interface UsageData {
    count: number;
    plan: string;
    total_chats: number;
    error_rate: number;
    busiest_hour: string;
    is_trial_active?: boolean;
    trial_ends_in_days?: number;
    trial_end_date?: string;
    is_payment_valid: boolean;
}

const PricingPlans: React.FC = () => {
    const [billingCycle, setBillingCycle] = useState<"monthly" | "yearly">("monthly");
    const [showModal, setShowModal] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
    const [currentPlan, setCurrentPlan] = useState<string | null>(null);
    const [pricingPlans, setPricingPlans] = useState<any[]>([]);
    const [token, setToken] = useState<string | null>(null);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelMessage, setCancelMessage] = useState("");
    const [showConfirmUpgrade, setShowConfirmUpgrade] = useState(false);
    const [targetPlan, setTargetPlan] = useState<any | null>(null);
    const [isPaymentValid, setIsPaymentValid] = useState<boolean>(true);

    // const [isTrialActive, setIsTrialActive] = useState(false);
    // const [hasTrialEnded, setHasTrialEnded] = useState(false);
    const [trialStatus, setTrialStatus] = useState<"active" | "ended" | "none">("none");




    useEffect(() => {
        const t = localStorage.getItem("access_token");
        if (t) setToken(t);
    }, []);

    useEffect(() => {
        const fetchPlans = async () => {
            try {
                const res = await axios.get("/subscription/all_subscriptions");
                setPricingPlans(res.data as any[]);
            } catch (err) {
                console.error("Fetch failed", err);
            }
        };

        fetchPlans();
    }, []);

    useEffect(() => {
        const fetchCurrentPlan = async () => {
            try {
                const response = await axios.get<CurrentPlanResponse>("/subscription/current-plan");
                setCurrentPlan(response.data.plan_name  || null);
                setIsPaymentValid(response.data.is_payment_valid ?? true);
                // console.log("is payment valid? ", response.data.is_payment_valid);
            } catch (error) {
                console.error("Failed to fetch subscription plan:", error);
            }
        };
        fetchCurrentPlan();
    }, []);
    useEffect(() => {
        const fetchTrialStatus = async () => {
            try {
                const res = await axios.get<UsageData>("/bot/request-usage");

                const isActive = res.data?.is_trial_active;
                const trialEnds = res.data?.trial_ends_in_days;

                // console.log("Trial API raw response:", res.data);


                if (isActive) {
                    setTrialStatus("active");
                } else if (trialEnds !== undefined && trialEnds <= 0 && res.data.trial_end_date) {
                    setTrialStatus("ended");
                } else {
                    setTrialStatus("none"); // No trial ever started
                }
            } catch (err) {
                console.error("Failed to fetch trial status:", err);
            }
        };

        fetchTrialStatus();
    }, []);




    const allFeatures = Array.from(new Set(pricingPlans.flatMap(plan => plan.features || [])));

    const handleSubscription = async (plan: any) => {
        try {
            await axios.post("/subscription/save-selection", {
                headers: {
                    "Content-Type": "application/json",
                },
                plan_id: plan.id,
                billing_cycle: billingCycle,
            });

            const checkoutRes = await axios.post<CheckoutResponse>("/create-checkout-session", {
                headers: {
                    "Content-Type": "application/json",
                },
                price_id:
                    billingCycle === "monthly"
                        ? plan.stripe_amount_id_month
                        : plan.stripe_amount_id_year,
            });

            if (checkoutRes.data.url) {
                window.location.href = checkoutRes.data.url;
            } else {
                alert("Checkout error: " + checkoutRes.data.error);
            }
        } catch (error: any) {
            console.error("Subscription error:", error);
            alert(error.response?.data?.error || "Unexpected error occurred. See console.");
        }
    };

    const handleConfirmedCancellation = async () => {
        try {
            const token = localStorage.getItem("access_token");
            const res = await fetch(`${API_BASE_URL}/subscription/cancel-subscription`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (res.ok) {
                setCancelMessage("✅ Subscription canceled successfully.");
                setTimeout(() => {
                    setShowCancelModal(false);
                    window.location.reload(); // You can also refetch current plan if needed
                }, 1500);
            } else {
                const data = await res.json();
                setCancelMessage(`❌ Error: ${data.error}`);
            }
        } catch (err) {
            console.error(err);
            setCancelMessage("❌ An error occurred while canceling.");
        }
    };
    // useEffect(() => {
    //     console.log("🔥 Trial status changed:", trialStatus);
    // }, [trialStatus]);


    // const isCurrentAndValid = (planName: string) =>
    //     currentPlan === planName && isPaymentValid;


    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 p-6">
            <div className="bg-white shadow-lg rounded-lg p-6 w-full max-w-4xl">
                <h2 className="text-3xl font-extrabold text-gray-800 mb-6 text-center">💳 Choose Your Plan</h2>
                {trialStatus === "none" && (
                    <p className="text-center text-sm text-gray-600 mb-6">
                        🚀 <strong>Enjoy a 14-day free trial</strong> on all paid plans. No charges until your trial ends.
                    </p>
                )}

                {trialStatus === "active" && (
                    <p className="text-center text-sm text-green-600 mb-6">
                        🕒 <strong>Trial ongoing!</strong> You’re still enjoying full access.
                    </p>
                )}

                {trialStatus === "ended" && !isPaymentValid && (
                    <p className="text-center text-sm text-red-500 mb-6">
                        ⛔ Your free trial has ended. Please select a plan to continue using premium features.
                    </p>
                )}

                {isPaymentValid && currentPlan && currentPlan !== "Free" && (
                    <p className="text-center text-sm text-blue-600 mb-6">
                        ✅ You’re subscribed to the <strong>{currentPlan}</strong> plan.
                    </p>
                )}

                <div className="flex justify-center mb-6">
                    <button
                        className={`px-4 py-2 rounded-l ${billingCycle === "monthly" ? "bg-blue-600 text-white" : "bg-gray-300 text-gray-700"}`}
                        onClick={() => setBillingCycle("monthly")}
                    >
                        Monthly
                    </button>
                    <button
                        className={`px-4 py-2 rounded-r ${billingCycle === "yearly" ? "bg-blue-600 text-white" : "bg-gray-300 text-gray-700"}`}
                        onClick={() => setBillingCycle("yearly")}
                    >
                        Yearly <span className="text-sm text-green-600"> (Save 20%)</span>
                    </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/*{pricingPlans.map((plan) => (*/}
                    {/*    <div*/}
                    {/*        key={plan.name}*/}
                    {/*        className={`border p-6 rounded-lg shadow-md text-center transition transform hover:scale-105 ${plan.bestValue ? "border-blue-500" : "border-gray-300"}`}*/}
                    {/*    >*/}
                    {/*        {plan.bestValue && (*/}
                    {/*            <div*/}
                    {/*                className="text-xs uppercase bg-blue-500 text-white py-1 px-3 rounded-md w-fit mx-auto mb-2">*/}
                    {/*                Best Value*/}
                    {/*            </div>*/}
                    {/*        )}*/}
                    {/*        <h3 className="text-lg font-semibold text-gray-800">{plan.name}</h3>*/}
                    {/*        <p className="text-gray-500 text-lg">*/}
                    {/*            ${billingCycle === "monthly" ? plan.price_monthly : plan.price_yearly}/*/}
                    {/*            {billingCycle === "monthly" ? "month" : "year"}*/}
                    {/*        </p>*/}
                    {/*        <div className="flex justify-center">*/}
                    {/*            <ul className="text-sm text-gray-700 mt-4 space-y-2 text-left">*/}
                    {/*                {plan.features.map((feature: string, idx: number) => (*/}
                    {/*                    <li key={idx} className="flex items-center">*/}
                    {/*                        <span className="text-green-500 mt-1 mr-2"><CheckMark/></span>*/}
                    {/*                        <span>{feature}</span>*/}
                    {/*                    </li>*/}
                    {/*                ))}*/}
                    {/*            </ul>*/}
                    {/*        </div>*/}
                    {/*        <button*/}
                    {/*            onClick={() => {*/}
                    {/*                if (currentPlan && currentPlan !== plan.name) {*/}
                    {/*                    setTargetPlan(plan);*/}
                    {/*                    setShowConfirmUpgrade(true);*/}
                    {/*                } else {*/}
                    {/*                    handleSubscription(plan);*/}
                    {/*                }*/}
                    {/*            }}*/}
                    {/*            disabled={currentPlan === plan.name}*/}
                    {/*            className={`mt-4 w-full py-2 px-4 rounded-lg transition */}
                    {/*            ${currentPlan === plan.name*/}
                    {/*                                        ? "bg-green-500 text-white cursor-not-allowed"*/}
                    {/*                                        : "bg-blue-600 hover:bg-blue-700 text-white"}*/}
                    {/*        `}*/}
                    {/*        >*/}
                    {/*            {currentPlan === plan.name ? "✔ Current Plan" : "Subscribe"}*/}
                    {/*        </button>*/}
                    {/*        {currentPlan === plan.name && (*/}
                    {/*            <button*/}
                    {/*                onClick={() => {*/}
                    {/*                    setCancelMessage("");*/}
                    {/*                    setShowCancelModal(true);*/}
                    {/*                }}*/}
                    {/*                className="text-sm mt-2 underline text-red-500 hover:text-red-700"*/}
                    {/*            >*/}
                    {/*                Cancel Subscription*/}
                    {/*            </button>*/}

                    {/*        )}*/}
                    {/*    </div>*/}
                    {/*))}*/}
                    {pricingPlans.map((plan) => {
                        const isCurrentAndValid = currentPlan === plan.name && isPaymentValid;

                        return (
                            <div
                                key={plan.name}
                                className={`border p-6 rounded-lg shadow-md text-center transition transform hover:scale-105 ${plan.bestValue ? "border-blue-500" : "border-gray-300"}`}
                            >
                                {plan.bestValue && (
                                    <div
                                        className="text-xs uppercase bg-blue-500 text-white py-1 px-3 rounded-md w-fit mx-auto mb-2">
                                        Best Value
                                    </div>
                                )}
                                <h3 className="text-lg font-semibold text-gray-800">{plan.name}</h3>
                                <p className="text-gray-500 text-lg">
                                    ${billingCycle === "monthly" ? plan.price_monthly : plan.price_yearly}/
                                    {billingCycle === "monthly" ? "month" : "year"}
                                </p>

                                <div className="flex justify-center">
                                    <ul className="text-sm text-gray-700 mt-4 space-y-2 text-left">
                                        {plan.features.map((feature: string, idx: number) => (
                                            <li key={idx} className="flex items-center">
                                                <span className="text-green-500 mt-1 mr-2"><CheckMark/></span>
                                                <span>{feature}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <button
                                    onClick={() => {
                                        if (!isCurrentAndValid) {
                                            if (currentPlan && currentPlan !== plan.name) {
                                                setTargetPlan(plan);
                                                setShowConfirmUpgrade(true);
                                            } else {
                                                handleSubscription(plan);
                                            }
                                        }
                                    }}
                                    disabled={isCurrentAndValid}
                                    className={`mt-4 w-full py-2 px-4 rounded-lg transition ${
                                        isCurrentAndValid
                                            ? "bg-green-500 text-white cursor-not-allowed"
                                            : "bg-blue-600 hover:bg-blue-700 text-white"
                                    }`}
                                >
                                    {isCurrentAndValid ? "✔ Current Plan" : "Subscribe"}
                                </button>

                                {isCurrentAndValid && (
                                    <button
                                        onClick={() => {
                                            setCancelMessage("");
                                            setShowCancelModal(true);
                                        }}
                                        className="text-sm mt-2 underline text-red-500 hover:text-red-700"
                                    >
                                        Cancel Subscription
                                    </button>
                                )}
                            </div>
                        );
                    })}

                </div>
                <div className="text-center mt-6">
                    <button
                        className="text-blue-500 underline text-sm hover:text-blue-700"
                        onClick={() => setShowModal(true)}
                    >
                        Compare Plans
                    </button>
                </div>
                {showModal && (
                    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center">
                        <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-2xl">
                            <h3 className="text-lg font-bold mb-4">📊 Compare Plans</h3>
                            <table className="w-full border-collapse border border-gray-300">
                                <thead className="bg-gray-200">
                                <tr>
                                    <th className="p-2 border border-gray-300">Feature</th>
                                    {pricingPlans.map((plan) => (
                                        <th key={plan.name}
                                            className={`p-2 border border-gray-300 ${plan.bestValue ? "bg-blue-100" : ""}`}>
                                            {plan.name}
                                        </th>
                                    ))}
                                </tr>
                                </thead>
                                <tbody>
                                {allFeatures.map((feature, index) => (
                                    <tr key={index}>
                                        <td className="p-2 border">{feature}</td>
                                        {pricingPlans.map((plan) => (
                                            <td key={plan.name} className="p-2 border text-center">
                                                {plan.features.includes(feature) ? "✔" : "—"}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                            <div className="text-right mt-4">
                                <button className="bg-red-500 text-white px-4 py-2 rounded"
                                        onClick={() => setShowModal(false)}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                {showCancelModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg shadow-lg max-w-sm w-full p-6">
                            <h2 className="text-lg font-bold text-red-600 mb-4">Confirm Cancellation</h2>
                            <p className="text-gray-700 mb-6">Are you sure you want to cancel your subscription?</p>
                            <div className="flex justify-end space-x-4">
                                <button
                                    className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-1 px-4 rounded"
                                    onClick={() => setShowCancelModal(false)}
                                >
                                    No, Keep It
                                </button>
                                <button
                                    className="bg-red-500 hover:bg-red-600 text-white font-semibold py-1 px-4 rounded"
                                    onClick={handleConfirmedCancellation}
                                >
                                    Yes, Cancel
                                </button>
                            </div>

                            {cancelMessage && (
                                <p className="text-sm mt-4 text-center text-gray-600">{cancelMessage}</p>
                            )}
                        </div>
                    </div>
                )}
            </div>
            {showConfirmUpgrade && targetPlan && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg max-w-sm w-full p-6">
                        <h2 className="text-lg font-bold text-blue-600 mb-4">Confirm Plan Change</h2>
                        <p className="text-gray-700 mb-6">
                            You're about to switch from <strong>{currentPlan}</strong> to <strong>{targetPlan.name}</strong>. Do you want to continue?
                        </p>
                        <div className="flex justify-end space-x-4">
                            <button
                                className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-1 px-4 rounded"
                                onClick={() => setShowConfirmUpgrade(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-1 px-4 rounded"
                                onClick={() => {
                                    handleSubscription(targetPlan);
                                    setShowConfirmUpgrade(false);
                                }}
                            >
                                Yes, Continue
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default PricingPlans;
